import { useAppDispatch, useAppSelector } from '../useReduxToolkit';
import { type WaitingMember } from '../../shared/waitingMember';
import { useService } from '../useService';

import { getIdentityId } from '../../features/realtime/realtime.selectors';
import { RealtimeRole } from '../../features/realtime/realtime.types';
import { type ApolloSession } from '../../features/apolloSession/apolloSession.types';
import {
  update as updateApolloSession,
  setSessionId as setApolloSessionId,
  setStartCallTime,
  setDbSessionId,
} from '../../features/apolloSession/apolloSession.slice';
import { setSessionId } from '../../features/session/session.slice';

import { Vendor } from '../../webrtc/media';
import { Platform } from '../../services';
import { useRouter } from 'next/router';
import { getPublicRuntimeConfig } from '../../libs/init/getPublicRuntimeConfig';
import { type ProviderCallService } from '../../services/call';
import { SessionType } from '../../webrtc/session';
import { useUserSubscription } from '../useUserSubscription';
import {
  callInitTimer,
  CallInitStep,
} from '../useCallState/states/callInitTimer';
import { CallType } from '../../shared/types';
import { useGetAccessToken, useGetUserId } from '../useCredentials';
import { useHotpot } from '@doxyme/hotpot/react';

type StartCall = (
  participants: WaitingMember[],
  isAudioCall?: CallType,
) => void;

type UseStartCallReturn = {
  startCall: StartCall;
};

/**
 * For a provider to start a call with a patient.
 *
 * This does the following:
 * - manages the data needed to start a call
 * - redirects to the /call page
 * - calls the provider call service to
 *   start the call.
 *
 * TODO: Rename to useProviderStartCall
 */
export const useStartCall = (): UseStartCallReturn => {
  const userId = useGetUserId();
  const token = useGetAccessToken();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const callService = useService('call');

  const identityId = useAppSelector(getIdentityId);
  const { isPaidUser } = useUserSubscription();

  // for hotpot
  const hotpot = useHotpot();

  const startCall = (
    participants: WaitingMember[],
    callType = CallType.Video,
  ): void => {
    if (identityId === undefined) {
      throw new Error('No identityId provided');
    }

    const participantIds = participants.map((p) => p.id);

    if (participantIds.length === 0) {
      throw new Error('No participants provided');
    }

    const v2ApolloSession: Partial<ApolloSession> = {
      patientIds: participantIds,
      patientId: participantIds[0],
      role: RealtimeRole.Provider,
      vendor: Vendor.Vonage,
      navigationStarted: Date.now(),
      roomId: participants[0]?.roomId,
      identityId,
      patientPlatform: Platform.Web,
      isAudioCall: callType === CallType.Audio,
    };
    callInitTimer.start(CallInitStep.NavigationStarted, Date.now());

    dispatch(updateApolloSession(v2ApolloSession));
    void router.push({ pathname: getPublicRuntimeConfig().paths.call });

    void (callService as ProviderCallService)
      .start({
        participants: participantIds,
        isAudioCall: callType === CallType.Audio,
        sessionType: isPaidUser ? SessionType.Routed : SessionType.P2P,
        identityId,
        credentials: {
          userId,
          token,
        },
      })
      .then((result): void => {
        if (result?.sessionId && result?.dbSessionId) {
          dispatch(setApolloSessionId(result.sessionId));
          dispatch(setSessionId(result.sessionId));
          dispatch(setDbSessionId(result.dbSessionId));
          dispatch(setStartCallTime(Date.now()));

          // for hotpot
          for (const participantId of participantIds) {
            void hotpot?.visit.startCallByRealtimeId({
              realtimeId: participantId,
              sessionId: result.dbSessionId,
            });
          }
        }

        return undefined;
      });
  };

  return { startCall };
};
